import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import styled from 'styled-components';

import { ELEMENT_ACTION } from '../../shared/constants';
import { DOMAINS } from '../../shared/constants/Miscellaneous.constants';

import ListItem from '../../components/atoms/list-item/ListItem';

import DeqAnchorLink from '../deq/DeqAnchorLink';

interface ReactMarkdownProps {
  children: string;
  className?: string;
  linkTarget?: string;
  componentType?: string;
  components?: Components;
  inverted?: boolean;
}

const isMatchingDomain = (url?: string, match: boolean = true) => {
  if (!url) return false;

  const isMatch = [
    '/',
    DOMAINS.production,
    DOMAINS.open,
    DOMAINS.development,
    DOMAINS.acceptance,
  ].some((internalUrl) => url.startsWith(internalUrl));

  return match ? isMatch : !isMatch;
};

const isDoorpakkenDomain = (url?: string) => isMatchingDomain(url, true);

const isExternalDomain = (url?: string) => isMatchingDomain(url, false);

const preprocessMarkdown = (markdown: string) => {
  if (typeof markdown !== 'string') {
    return { cleanedMarkdown: '', customAttributesMap: {} }; // Return defaults if markdown is invalid
  }

  // Create a map to store custom attributes associated with each link
  const customAttributesMap: Record<string, string[]> = {};

  // Use regex to find links with custom attributes and store them in the map
  const cleanedMarkdown = markdown.replace(
    /\[([^\]]+)\]\(([^)]+)\)\{target:(self|blank)\}/g,
    (_, text, url, target) => {
      if (!customAttributesMap[url]) {
        customAttributesMap[url] = [];
      }
      customAttributesMap[url].push(target); // Store all custom attributes in a queue
      return `[${text}](${url})`; // Return the link without the custom attribute
    }
  );

  return { cleanedMarkdown, customAttributesMap };
};

const MarkdownAnchorLink = ({
  children,
  deqComponentType = 'react-markdown',
  deqActionType,
  node,
  customAttributesMap,
  ...props
}: {
  children: React.ReactNode;
  deqComponentType?: string;
  deqActionType?: string;
  customAttributesMap: Record<string, string[]>;
  [key: string]: any;
}) => {
  // Extract the custom attribute using the node's position in the map
  const customAttributeList = customAttributesMap[props.href];
  const customAttribute = customAttributeList
    ? customAttributeList.shift() // Access the next custom attribute in the queue
    : undefined;

  const isExternal =
    customAttribute === 'blank' ||
    (customAttribute === undefined &&
      (!isDoorpakkenDomain(props?.href) || isExternalDomain(props?.href)));
  const target = isExternal ? '_blank' : '_self';

  return (
    <DeqAnchorLink
      target={target}
      deqComponentType={deqComponentType}
      deqActionType={isExternal ? ELEMENT_ACTION.exit : undefined}
      {...props}
    >
      {children}
    </DeqAnchorLink>
  );
};

const CustomBoldFont = styled.strong`
  font-family: ${({ theme }) => theme.fontSemibold};
`;

const ReactMarkdownExtended = ({
  children,
  className,
  linkTarget,
  componentType,
  components,
  inverted = false,
  ...markdownProps
}: ReactMarkdownProps) => {
  if (typeof children !== 'string') {
    return null; // Return null to avoid rendering invalid content
  }

  const { customAttributesMap } = preprocessMarkdown(children);

  const cleanedMarkdownText = children.replace(/\{target:[^}]+\}/g, '');

  return (
    <ReactMarkdown
      data-testid="markdown"
      components={{
        // eslint-disable-next-line react/no-unstable-nested-components
        a: ({ node, ...props }) => (
          <MarkdownAnchorLink
            deqComponentType={componentType}
            node={node}
            customAttributesMap={customAttributesMap}
            {...props}
            {...markdownProps}
          >
            {props.children}
          </MarkdownAnchorLink>
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        li: (props) => <ListItem inverted={inverted} {...props} />,
        strong: CustomBoldFont,
        ...components,
      }}
      className={className}
    >
      {cleanedMarkdownText}
    </ReactMarkdown>
  );
};

export default ReactMarkdownExtended;
